import type {TextProps} from '@primer/react'
import {Box, Text} from '@primer/styled-react'
// eslint-disable-next-line @github-ui/github-monorepo/no-sx
import type {SxProp} from '@primer/styled-react'
import type {PolymorphicComponent} from '@github-ui/react-polymorphic'
import DOMPurify, {type Config as DOMPurifyConfig} from 'dompurify'
import type React from 'react'

interface UnsafeHTMLProps {
  /**
   * Set the rendered HTML of the component. This HTML will always be sanitized by DOMPurify
   * before being rendered to prevent XSS attacks.
   */
  html: string
  /**
   * Optional config passed to DOMPurify when sanitizing HTML.
   */
  domPurifyConfig?: DOMPurifyConfig
}

function getSanitizedHTMLAndProps<T>(propsWithHtml: T & UnsafeHTMLProps) {
  const {html, domPurifyConfig, ...props} = propsWithHtml
  const config = {
    ...domPurifyConfig,
    // we want to ensure we are returning a string not a dom node
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  } satisfies DOMPurifyConfig

  return {
    // Run the HTML through DOMPurify to sanitize it
    sanitizedHTML: DOMPurify.sanitize(html, config),
    props: props as unknown as T,
  }
}

/**
 * @deprecated Use `UnsafeHTMLDiv` or `UnsafeHTMLText` instead.
 *
 * `UnsafeHTMLBox` extends `Box` from `@primer/react` with props for rendering HTML strings
 * that need to be sanitized. The HTML will always be run through DOMPurify before being rendered
 * to prevent XSS attacks.
 */
// eslint-disable-next-line @github-ui/github-monorepo/no-sx
export const UnsafeHTMLBox = withSanitizedHTML<SxProp>(Box) as PolymorphicComponent<
  'div' | 'span' | 'pre' | 'table' | 'tbody' | 'tr' | 'td' | 'ul' | 'ol' | 'li',
  // eslint-disable-next-line @github-ui/github-monorepo/no-sx
  SxProp & UnsafeHTMLProps
>

/**
 * `UnsafeHTMLText` extends `Text` from `@primer/react` with props for rendering HTML strings
 * that need to be sanitized. The HTML will always be run through DOMPurify before being rendered
 * to prevent XSS attacks.
 */
export const UnsafeHTMLText = withSanitizedHTML<TextProps>(Text) as PolymorphicComponent<
  'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a',
  TextProps<'div' | 'span' | 'p' | 'strong' | 'em' | 'pre' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'a'> &
    UnsafeHTMLProps
>

type DivProps = UnsafeHTMLProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

const Div = ({ref, ...props}: DivProps & {ref?: React.Ref<HTMLDivElement>}) => <div {...props} ref={ref} />
Div.displayName = 'Div'

/**
 * `UnsafeHTMLDiv` extends `div` with props for rendering HTML strings
 * that need to be sanitized. The HTML will always be run through DOMPurify before being rendered
 * to prevent XSS attacks.
 */
export const UnsafeHTMLDiv = withSanitizedHTML<DivProps>(Div)

/**
 * A higher-order component that extends a basic component by offering
 * an `html` prop that always sanitizes its content via DOMPurify.
 */
function withSanitizedHTML<Props extends object>(Component: React.ComponentType<Props>) {
  const UnsafeHTMLComponent = (propsWithHtml: Props & UnsafeHTMLProps) => {
    const {sanitizedHTML, props} = getSanitizedHTMLAndProps(propsWithHtml)
    // This is the only place in the codebase where `dangerouslySetInnerHTML` should be allowed
    // eslint-disable-next-line react/forbid-component-props
    return <Component {...props} dangerouslySetInnerHTML={{__html: sanitizedHTML}} />
  }
  UnsafeHTMLComponent.displayName = `UnsafeHTML${Component.displayName || Component.name}`

  return UnsafeHTMLComponent
}

try{ UnsafeHTMLBox.displayName ||= 'UnsafeHTMLBox' } catch {}
try{ UnsafeHTMLText.displayName ||= 'UnsafeHTMLText' } catch {}
try{ UnsafeHTMLDiv.displayName ||= 'UnsafeHTMLDiv' } catch {}